import { useCallback } from 'react';
import Swal from 'sweetalert2';
import { 
  handleDeleteThread, 
  handleDeleteComment,
  editThreadForm,
  editComment 
} from '../../../utils/ApiCall';

export const useThreadActions = ({
  pathname,
  row,
  setThreads,
  setNumberOfThreads
}) => {
  const handleThreadDelete = useCallback(async (thread) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro que desea eliminar este thread?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const response = await handleDeleteThread(pathname, thread, row);
        const newThreads = response.data.threads;

        setThreads(newThreads);
        if (setNumberOfThreads) {
          setNumberOfThreads(newThreads.length);
        }

        await Swal.fire("Tu thread ha sido eliminado", "", "success");
      }
    } catch (error) {
      console.error("Error al eliminar el thread:", error);
      await Swal.fire("Error", "Error al eliminar el thread", "error");
    }
  }, [pathname, row, setThreads, setNumberOfThreads]);

  const handleCommentDelete = useCallback(async (commentId) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro que desea eliminar este comentario?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const response = await handleDeleteComment(pathname, commentId, row);
        const newThreads = response.data.threads;
        
        setThreads(newThreads);
        await Swal.fire("El comentario ha sido eliminado", "", "success");
      }
    } catch (error) {
      console.error("Error al eliminar el comentario:", error);
      await Swal.fire("Error", "Error al eliminar el comentario", "error");
    }
  }, [pathname, row, setThreads]);

  const handleThreadEdit = useCallback(async (threadId, content, files, removedFiles, threadKey, board) => {
    try {
      const editedThread = await editThreadForm(
        threadKey,
        threadId,
        content,
        board,
        row.id,
        files,
        []
      );

      if (editedThread?.data?.threads) {
        const updatedThread = editedThread.data.threads.find(
          (thread) => thread.id === threadId
        );

        setThreads((prevThreads) =>
          prevThreads.map((thread) => {
            if (thread.id === threadId) {
              const currentMedia = thread.media || [];
              const remainingMedia = currentMedia.filter(
                (media) => !removedFiles.some((file) => file.id === media.id)
              );
              return {
                ...thread,
                content: updatedThread?.content ?? content,
                media: remainingMedia.concat(updatedThread?.media ?? []),
              };
            }
            return thread;
          })
        );
      } else {
        throw new Error("No updated threads found in response");
      }
    } catch (error) {
      console.error("Error al actualizar el thread:", error);
      throw error;
    }
  }, [row, setThreads]);

  const handleCommentEdit = useCallback(async (threadId, commentId, content, files, removedFiles, threadKey, board) => {
    try {
      const editedComment = await editComment(
        threadKey,
        commentId,
        content,
        board,
        row.id,
        files,
        []
      );

      if (editedComment?.data?.threads) {
        const updatedThread = editedComment.data.threads.find(
          (thread) => thread.id === threadId
        );

        if (!updatedThread) {
          throw new Error("Updated thread not found in response");
        }

        const updatedComment = updatedThread.comments.find(
          (comment) => comment.id === commentId
        );

        if (!updatedComment) {
          throw new Error("Updated comment not found in response");
        }

        setThreads((prevThreads) =>
          prevThreads.map((thread) => {
            if (thread.id === threadId) {
              return {
                ...thread,
                comments: thread.comments.map((comment) => {
                  if (comment.id === commentId) {
                    const currentMedia = comment.media || [];
                    const remainingMedia = currentMedia.filter(
                      (media) =>
                        !removedFiles.some((file) => file.id === media.id)
                    );

                    return {
                      ...comment,
                      content: updatedComment?.content ?? content,
                      media: remainingMedia.concat(updatedComment?.media ?? []),
                    };
                  }
                  return comment;
                }),
              };
            }
            return thread;
          })
        );
      } else {
        throw new Error("No updated threads found in response");
      }
    } catch (error) {
      console.error("Error al actualizar el comentario:", error);
      throw error;
    }
  }, [row, setThreads]);

  return {
    handleThreadDelete,
    handleCommentDelete,
    handleThreadEdit,
    handleCommentEdit
  };
};